<template>
  <div id="app">
    <!-- Show impersonation alert for impersonated user sessions -->
    <impersonation-alert v-if="isImpersonating" />
    
    <!-- Only show navbar and footer for non-admin routes -->
    <div class="navbar-wrapper">
      <Navbar v-if="!isAdminRoute" ref="navbar" />
    </div>
    <subscription-status v-if="!isAdminRoute && isLoggedIn" />
    <main :class="{ 'admin-main': isAdminRoute }">
      <router-view />
    </main>
    <site-footer v-if="!isAdminRoute" />
  </div>
</template>

<script>
  import Navbar from '@/components/MainNavbar.vue'
  import SiteFooter from '@/components/SiteFooter.vue'
  import ImpersonationAlert from '@/components/admin/ImpersonationAlert.vue'
  import SubscriptionStatus from '@/components/SubscriptionStatus.vue'

  export default {
    name: 'App',
    components: {
      Navbar,
      SiteFooter,
      ImpersonationAlert,
      SubscriptionStatus
    },
    data() {
      return {
        // Path prefixes that should be considered admin routes - IMPORTANT: be specific with /admin/ to avoid matching /dashboard
        adminRoutes: ['/admin/'],
        isImpersonating: false
      }
    },
    methods: {
      // Centralized authentication check that can be called from any component
      checkAuthentication() {
        const token = localStorage.getItem('authToken');
        const isAuthenticated = !!token;
        
        if (!isAuthenticated) {
          this.$router.push('/login');
          return false;
        }
        
        return true;
      },
    },
    computed: {
      // Check if current route is an admin route
      isAdminRoute() {
        if (!this.$route || !this.$route.path) return false;
        
        const path = this.$route.path;
        
        // Special case: explicitly check that dashboard is never considered an admin route
        if (path === '/dashboard' || path.startsWith('/dashboard/')) {
          console.log('Dashboard route detected, showing navbar');
          return false;
        }
        
        // Check if the current path exactly matches /admin or starts with any admin route prefix
        const isAdminPath = path === '/admin' || this.adminRoutes.some(prefix => path.startsWith(prefix));
        
        // Get user role from localStorage to ensure we're showing the navbar for regular users
        // even if they visit an admin path (they'll be redirected by router guards)
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userRole = userData.role_details?.name || 
                        (userData.role && typeof userData.role === 'object' ? userData.role.name : userData.role);
        const isAdmin = userRole === 'admin';
        
        console.log('Navbar visibility check:', {
          path: path,
          isAdminPath,
          userRole,
          isAdmin,
          hideNavbar: isAdminPath && isAdmin,
          showNavbar: !(isAdminPath && isAdmin)
        });
        
        // Only consider it an admin route if both the path is admin AND user is admin
        return isAdminPath && isAdmin;
      },
      isLoggedIn() {
        return !!this.$store.state.token && !!this.$store.state.user;
      }
    },
    created() {
      // Check for existing login on app startup
      // FIXED: Get authToken (correct name) instead of token to match API interceptor
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('user');
      
      // Check if user is in impersonation mode
      this.isImpersonating = localStorage.getItem('isImpersonating') === 'true';
      
      if (token && userData) {
        // Load user data into Vuex store
        this.$store.commit('setAuthToken', token);
        this.$store.commit('setUser', JSON.parse(userData));
        console.log("User session restored from localStorage with token:", token.substring(0, 10) + '...');
      }
      
      // Log every component mount for debugging
      this.$nextTick(() => {
        console.log("App mounted with components:", {
          hasNavbar: !!this.$refs.navbar,
          route: this.$route?.path,
          isAdminRoute: this.isAdminRoute,
          showingNavbar: !this.isAdminRoute
        });
      });
    },
    watch: {
      // Watch for route changes to update admin status
      '$route'(to) {
        console.log('Route changed:', to.path, 'isAdminRoute:', this.isAdminRoute);
      }
    }
  }
</script>

<style lang="scss">
@import 'bootstrap/dist/css/bootstrap.css';
@import '@/assets/css/buttons.css';

:root {
  --primary-color: #00CEC9; /* Teal color from logo */
  --secondary-color: #FFFFFF; /* White from logo */
  --dark-color: #000000; /* Black from logo */
  --light-color: #F8F9FA;
  --gray-color: #6C757D;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.navbar-wrapper {
  position: relative;
  z-index: 1050; /* Ensure it's consistent with bootstrap z-indexes and navbar component */
}

body {
  font-family: 'Roboto', sans-serif;
  line-height: 1.6;
  color: var(--dark-color);
  background-color: var(--light-color);
}

a {
  text-decoration: none;
  color: var(--primary-color);
  
  &:hover {
    color: darken(#00CEC9, 10%);
  }
}

/* Removed duplicate .btn-primary styles - now handled in buttons.css */

.container {
  padding: 20px;
}

main {
  min-height: calc(100vh - 150px);
  padding-top: 120px; /* Increased padding to account for fixed navbar */
  position: relative;
  z-index: 1;
  overflow-x: hidden; /* Prevent horizontal scrollbars */
}

/* Admin specific main content area */
.admin-main {
  min-height: 100vh;
  padding: 0;
  margin: 0;
}
</style>